import {
  Business as BusinessIcon,
  Calculate as CalculateIcon,
  ElectricBolt as ElectricIcon,
  Factory as FactoryIcon,
  Home as HomeIcon,
  Info as InfoIcon,
  MonetizationOn as MoneyIcon,
  Phone as PhoneIcon,
  Schedule as ScheduleIcon,
  WbSunny as SunIcon,
  TrendingUp as TrendingUpIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import SolarCalculatorApiService from "../../../infrastructure/api/solarCalculator";
import SolarROIChart from "./components/SolarROIChart";

const SolarCalculator = () => {
  // Form state
  const [formData, setFormData] = useState({
    province: "",
    city: "",
    powerCapacity: "",
    consumptionType: "residential",
    installationCostPerKW: 35000000, // Default 35M tomans
    electricityTariff: 3000, // Default 3000 tomans
    roofArea: "",
    contactName: "",
    contactMobile: "",
    contactEmail: "",
    requestConsultation: false,
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [provinces, setProvinces] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);
  const [suggestedPrices, setSuggestedPrices] = useState(null);
  const [calculationResult, setCalculationResult] = useState(null);
  const [showConsultationForm, setShowConsultationForm] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    // Update cities when province changes
    if (formData.province) {
      const selectedProvince = provinces.find(
        (p) => p.province === formData.province
      );
      if (selectedProvince) {
        setAvailableCities(selectedProvince.cities);
        setFormData((prev) => ({ ...prev, city: "" })); // Reset city
      }
    }
  }, [formData.province, provinces]);

  useEffect(() => {
    // Update default tariff when consumption type changes
    if (suggestedPrices && formData.consumptionType) {
      const typeConfig = suggestedPrices.consumptionTypes?.find(
        (t) => t.value === formData.consumptionType
      );
      if (typeConfig) {
        setFormData((prev) => ({
          ...prev,
          electricityTariff: typeConfig.defaultTariff,
        }));
      }
    }
  }, [formData.consumptionType, suggestedPrices]);

  const loadInitialData = async () => {
    try {
      const [provincesData, pricesData] = await Promise.all([
        SolarCalculatorApiService.getProvincesAndCities(),
        SolarCalculatorApiService.getSuggestedPrices(),
      ]);

      if (provincesData.isSuccess) {
        setProvinces(provincesData.data);
      }

      if (pricesData.isSuccess) {
        setSuggestedPrices(pricesData.data);
        setFormData((prev) => ({
          ...prev,
          installationCostPerKW: pricesData.data.installationCostPerKW.average,
        }));
      }
    } catch (error) {
      console.error("Error loading initial data:", error);
      toast.error("خطا در بارگذاری اطلاعات اولیه");
    }
  };

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCalculate = async () => {
    try {
      // Validation
      if (!formData.province || !formData.city) {
        toast.error("لطفاً استان و شهر را انتخاب کنید");
        return;
      }

      if (!formData.powerCapacity || formData.powerCapacity <= 0) {
        toast.error("لطفاً توان نیروگاه را وارد کنید");
        return;
      }

      if (
        !formData.installationCostPerKW ||
        formData.installationCostPerKW <= 0
      ) {
        toast.error("لطفاً هزینه نصب هر کیلووات را وارد کنید");
        return;
      }

      if (!formData.electricityTariff || formData.electricityTariff <= 0) {
        toast.error("لطفاً تعرفه برق را وارد کنید");
        return;
      }

      setLoading(true);

      const calculationInput = {
        province: formData.province,
        city: formData.city,
        powerCapacity: parseFloat(formData.powerCapacity),
        consumptionType: formData.consumptionType,
        installationCostPerKW: parseFloat(formData.installationCostPerKW),
        electricityTariff: parseFloat(formData.electricityTariff),
        roofArea: formData.roofArea ? parseFloat(formData.roofArea) : null,
        contactName: formData.contactName,
        contactMobile: formData.contactMobile,
        contactEmail: formData.contactEmail,
        requestConsultation: formData.requestConsultation,
      };

      const response = await SolarCalculatorApiService.calculateSolarSystem(
        calculationInput
      );

      if (response.isSuccess) {
        setCalculationResult(response.data);
        toast.success("محاسبه با موفقیت انجام شد");

        // Scroll to results
        setTimeout(() => {
          const resultsElement = document.getElementById("calculation-results");
          if (resultsElement) {
            resultsElement.scrollIntoView({ behavior: "smooth" });
          }
        }, 100);
      } else {
        toast.error(response.message || "خطا در محاسبه");
      }
    } catch (error) {
      console.error("Calculation error:", error);
      toast.error("خطا در محاسبه. لطفاً دوباره امتحان کنید.");
    } finally {
      setLoading(false);
    }
  };

  const handleRequestConsultation = async () => {
    try {
      if (!formData.contactName || !formData.contactMobile) {
        toast.error("لطفاً نام و شماره تماس را وارد کنید");
        return;
      }

      setLoading(true);

      const consultationData = {
        ...formData,
        powerCapacity: parseFloat(formData.powerCapacity || 0),
        installationCostPerKW: parseFloat(formData.installationCostPerKW),
        electricityTariff: parseFloat(formData.electricityTariff),
        roofArea: formData.roofArea ? parseFloat(formData.roofArea) : null,
        requestConsultation: true,
      };

      const response = await SolarCalculatorApiService.requestConsultation(
        consultationData
      );

      if (response.isSuccess) {
        toast.success(
          "درخواست مشاوره رایگان شما ثبت شد. کارشناسان ما به زودی با شما تماس خواهند گرفت."
        );
        setShowConsultationForm(false);
        setFormData((prev) => ({
          ...prev,
          contactName: "",
          contactMobile: "",
          contactEmail: "",
        }));
      } else {
        toast.error(response.message || "خطا در ثبت درخواست مشاوره");
      }
    } catch (error) {
      console.error("Consultation request error:", error);
      toast.error("خطا در ثبت درخواست مشاوره");
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (number) => {
    return new Intl.NumberFormat("fa-IR").format(Math.round(number));
  };

  const formatCurrency = (amount) => {
    return `${formatNumber(amount)} ریال`;
  };

  const getConsumptionTypeIcon = (type) => {
    switch (type) {
      case "residential":
        return <HomeIcon />;
      case "commercial":
        return <BusinessIcon />;
      case "industrial":
        return <FactoryIcon />;
      default:
        return <HomeIcon />;
    }
  };

  return (
    <Box
      sx={{ bgcolor: "background.default", minHeight: "100vh", py: 4 }}
      dir="rtl"
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Paper
          elevation={2}
          sx={{
            p: 4,
            mb: 4,
            textAlign: "center",
            bgcolor: "primary.main",
            color: "white",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2,
            }}
          >
            <SunIcon sx={{ fontSize: 48, mr: 2 }} />
            <Typography variant="h3" component="h1" fontWeight="bold">
              ماشین حساب نیروگاه خورشیدی خانگی
            </Typography>
          </Box>
          <Typography variant="h6" sx={{ opacity: 0.9 }}>
            محاسبه دقیق هزینه، درآمد و بازگشت سرمایه نیروگاه خورشیدی شما
          </Typography>
        </Paper>

        <Grid container spacing={4}>
          {/* Input Form */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography
                variant="h5"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", mb: 3 }}
              >
                <CalculateIcon sx={{ mr: 1 }} />
                اطلاعات نیروگاه خورشیدی
              </Typography>

              <Grid container spacing={3}>
                {/* Location */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>استان</InputLabel>
                    <Select
                      value={formData.province}
                      label="استان"
                      onChange={handleInputChange("province")}
                    >
                      {provinces.map((province, index) => (
                        <MenuItem key={index} value={province.province}>
                          {province.province}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth disabled={!formData.province}>
                    <InputLabel>شهر</InputLabel>
                    <Select
                      value={formData.city}
                      label="شهر"
                      onChange={handleInputChange("city")}
                    >
                      {availableCities.map((city, index) => (
                        <MenuItem key={index} value={city}>
                          {city}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Power Capacity */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="توان نیروگاه"
                    type="number"
                    value={formData.powerCapacity}
                    onChange={handleInputChange("powerCapacity")}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">کیلووات</InputAdornment>
                      ),
                    }}
                    helperText="معمولاً بین 3 تا 20 کیلووات برای منازل"
                  />
                </Grid>

                {/* Roof Area */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="مساحت سقف (اختیاری)"
                    type="number"
                    value={formData.roofArea}
                    onChange={handleInputChange("roofArea")}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">متر مربع</InputAdornment>
                      ),
                    }}
                    helperText="برای بررسی امکان نصب"
                  />
                </Grid>

                {/* Consumption Type */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    نوع مصرف
                  </Typography>
                  <RadioGroup
                    row
                    value={formData.consumptionType}
                    onChange={handleInputChange("consumptionType")}
                  >
                    <FormControlLabel
                      value="residential"
                      control={<Radio />}
                      label={
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <HomeIcon sx={{ mr: 1 }} />
                          خانگی
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="commercial"
                      control={<Radio />}
                      label={
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <BusinessIcon sx={{ mr: 1 }} />
                          تجاری
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="industrial"
                      control={<Radio />}
                      label={
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <FactoryIcon sx={{ mr: 1 }} />
                          صنعتی
                        </Box>
                      }
                    />
                  </RadioGroup>
                </Grid>

                {/* Installation Cost */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="هزینه نصب هر کیلووات"
                    type="number"
                    value={formData.installationCostPerKW}
                    onChange={handleInputChange("installationCostPerKW")}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          ریال
                          <Tooltip title="متوسط بازار: 25 تا 45 میلیون ریال">
                            <IconButton size="small">
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                    helperText={
                      suggestedPrices
                        ? `پیشنهادی: ${formatNumber(
                            suggestedPrices.installationCostPerKW.min
                          )} - ${formatNumber(
                            suggestedPrices.installationCostPerKW.max
                          )} ریال`
                        : "شامل پنل، اینورتر و نصب"
                    }
                  />
                </Grid>

                {/* Electricity Tariff */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="تعرفه برق یا قیمت خرید"
                    type="number"
                    value={formData.electricityTariff}
                    onChange={handleInputChange("electricityTariff")}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          ریال/کیلووات ساعت
                          <Tooltip title="تعرفه فعلی برق یا قیمت خرید برق تولیدی">
                            <IconButton size="small">
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                    helperText="بر اساس نوع مصرف و قیمت فعلی برق"
                  />
                </Grid>

                {/* Calculate Button */}
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleCalculate}
                    disabled={loading}
                    startIcon={
                      loading ? (
                        <CircularProgress size={20} />
                      ) : (
                        <CalculateIcon />
                      )
                    }
                    sx={{ py: 2, fontSize: "1.1rem" }}
                  >
                    {loading ? "در حال محاسبه..." : "محاسبه نیروگاه خورشیدی"}
                  </Button>
                </Grid>

                {/* Consultation Button */}
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    onClick={() => setShowConsultationForm(true)}
                    startIcon={<PhoneIcon />}
                    sx={{ py: 1.5 }}
                  >
                    درخواست مشاوره رایگان
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            {/* Consultation Form Modal */}
            {showConsultationForm && (
              <Paper
                elevation={4}
                sx={{
                  p: 3,
                  mt: 2,
                  border: "2px solid",
                  borderColor: "primary.main",
                }}
              >
                <Typography variant="h6" gutterBottom>
                  درخواست مشاوره رایگان
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="نام و نام خانوادگی"
                      value={formData.contactName}
                      onChange={handleInputChange("contactName")}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="شماره موبایل"
                      value={formData.contactMobile}
                      onChange={handleInputChange("contactMobile")}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="ایمیل (اختیاری)"
                      type="email"
                      value={formData.contactEmail}
                      onChange={handleInputChange("contactEmail")}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Button
                        variant="contained"
                        onClick={handleRequestConsultation}
                        disabled={loading}
                        startIcon={
                          loading ? (
                            <CircularProgress size={20} />
                          ) : (
                            <PhoneIcon />
                          )
                        }
                      >
                        ارسال درخواست
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => setShowConsultationForm(false)}
                      >
                        انصراف
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            )}
          </Grid>

          {/* Results */}
          <Grid item xs={12} md={6}>
            {calculationResult ? (
              <Box id="calculation-results">
                <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center", mb: 3 }}
                  >
                    <TrendingUpIcon sx={{ mr: 1 }} />
                    نتایج محاسبه
                  </Typography>

                  <Grid container spacing={2}>
                    {/* Total Cost */}
                    <Grid item xs={12} sm={6}>
                      <Card
                        sx={{
                          bgcolor: "error.light",
                          color: "error.contrastText",
                        }}
                      >
                        <CardContent>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <MoneyIcon sx={{ mr: 1 }} />
                            <Typography variant="h6">هزینه کل نصب</Typography>
                          </Box>
                          <Typography variant="h4">
                            {formatCurrency(
                              calculationResult.totalInstallationCost
                            )}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Annual Production */}
                    <Grid item xs={12} sm={6}>
                      <Card
                        sx={{
                          bgcolor: "warning.light",
                          color: "warning.contrastText",
                        }}
                      >
                        <CardContent>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <ElectricIcon sx={{ mr: 1 }} />
                            <Typography variant="h6">تولید سالانه</Typography>
                          </Box>
                          <Typography variant="h4">
                            {formatNumber(
                              calculationResult.annualEnergyProduction
                            )}{" "}
                            kWh
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Annual Income */}
                    <Grid item xs={12} sm={6}>
                      <Card
                        sx={{
                          bgcolor: "success.light",
                          color: "success.contrastText",
                        }}
                      >
                        <CardContent>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <TrendingUpIcon sx={{ mr: 1 }} />
                            <Typography variant="h6">درآمد سالانه</Typography>
                          </Box>
                          <Typography variant="h4">
                            {formatCurrency(calculationResult.annualIncome)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Payback Period */}
                    <Grid item xs={12} sm={6}>
                      <Card
                        sx={{
                          bgcolor: "info.light",
                          color: "info.contrastText",
                        }}
                      >
                        <CardContent>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <ScheduleIcon sx={{ mr: 1 }} />
                            <Typography variant="h6">بازگشت سرمایه</Typography>
                          </Box>
                          <Typography variant="h4">
                            {calculationResult.paybackPeriodYears.toFixed(1)}{" "}
                            سال
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* 25 Year Income */}
                    <Grid item xs={12}>
                      <Card
                        sx={{
                          bgcolor: "primary.light",
                          color: "primary.contrastText",
                        }}
                      >
                        <CardContent>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <MoneyIcon sx={{ mr: 1 }} />
                            <Typography variant="h6">
                              سود خالص 25 ساله
                            </Typography>
                          </Box>
                          <Typography variant="h3">
                            {formatCurrency(calculationResult.income25Years)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Technical Details */}
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        جزئیات فنی
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="body1">
                            <strong>مساحت مورد نیاز سقف:</strong>
                            <br />
                            {formatNumber(
                              calculationResult.requiredRoofArea
                            )}{" "}
                            متر مربع
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="body1">
                            <strong>ساعات آفتابی روزانه:</strong>
                            <br />
                            {calculationResult.sunshineHours} ساعت
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="body1">
                            <strong>بازده سیستم:</strong>
                            <br />
                            {calculationResult.systemEfficiency.toFixed(1)}%
                          </Typography>
                        </Grid>
                      </Grid>

                      {formData.roofArea &&
                        calculationResult.requiredRoofArea >
                          parseFloat(formData.roofArea) && (
                          <Alert severity="warning" sx={{ mt: 2 }}>
                            مساحت سقف شما ({formatNumber(formData.roofArea)} متر
                            مربع) کمتر از مساحت مورد نیاز است. لطفاً توان
                            نیروگاه را کاهش دهید یا مساحت بیشتری را در نظر
                            بگیرید.
                          </Alert>
                        )}
                    </Grid>
                  </Grid>
                </Paper>

                {/* ROI Chart */}
                {calculationResult.roiChartData && (
                  <SolarROIChart
                    roiData={calculationResult.roiChartData}
                    totalInstallationCost={
                      calculationResult.totalInstallationCost
                    }
                  />
                )}

                {/* Call to Action */}
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    textAlign: "center",
                    bgcolor: "success.main",
                    color: "white",
                    mt: 3,
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    آیا از نتایج راضی هستید؟
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    برای دریافت مشاوره تخصصی و شروع پروژه نیروگاه خورشیدی با ما
                    تماس بگیرید
                  </Typography>
                  <Button
                    variant="contained"
                    color="secondary"
                    size="large"
                    onClick={() => setShowConsultationForm(true)}
                    startIcon={<PhoneIcon />}
                  >
                    درخواست مشاوره رایگان
                  </Button>
                </Paper>
              </Box>
            ) : (
              <Paper
                elevation={2}
                sx={{ p: 3, textAlign: "center", color: "text.secondary" }}
              >
                <SunIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6">
                  اطلاعات نیروگاه خورشیدی خود را وارد کرده و دکمه محاسبه را
                  بزنید
                </Typography>
              </Paper>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default SolarCalculator;
